import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import Table from '../../components/UI/Table';
import Dropdown from '../../components/UI/Dropdown';
import './Tenants.css';
import tenantsSearch from '../../assets/icons/devices-search.svg';
import AddTenantModal from './AddTenantModal';
import EditTenantModal from './EditTenantModal';
import { useDispatch } from 'react-redux';
import { updateThemeLocal, saveTheme, updateImageFileName, resetTheme } from '../../store/themeSlice';
import ConfirmationModal from '../../components/UI/ConfirmationModal';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';

const Tenants = () => {
  const [pagesData, setPagesData] = useState({});
  const [pageCounts, setPageCounts] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [tenantToRemove, setTenantToRemove] = useState(null);
  const [showTenantInfoModal, setShowTenantInfoModal] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const itemsPerPage = 12;
  const [isReloading, setIsReloading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchResults, setSearchResults] = useState(null);
  const pagesPerFetch = Math.ceil(100 / itemsPerPage);
  const dispatch = useDispatch();

  const defaultTheme = {
    'color-primary': 'rgb(75, 108, 135)',
    'color-secondary': '#333',
    'color-bg': 'rgb(244, 243, 248)',
    'color-sidebar-bg': 'white',
    'color-text': '#ffffff',
    'color-landing-left': '#fff',
    'color-landing-right': 'linear-gradient(174deg, #BC7E30 4%, #ECBE84 52.8%)',
  };

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        await loadBlock(0, 1);
      } catch (error) {
        setErrorMessage('Failed to load tenants.');
        setShowErrorModal(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitial();
  }, []);

  useEffect(() => {
    const reloadData = async () => {
      try {
        await loadBlock(0, 1);
      } catch (error) {
        setErrorMessage('Failed to reload tenants.');
        setShowErrorModal(true);
      } finally {
        setIsReloading(false);
      }
    };
    if (isReloading) reloadData();
  }, [isReloading]);

  const loadBlock = async (blockIndex, desiredPage) => {
    if (pageCounts[blockIndex] && pagesData[desiredPage]) {
      setCurrentPage(desiredPage);
      return;
    }
    try {
      let res;
      if (blockIndex === 0) {
        res = await apiClient.get('/tenants');
      } else {
        const prevToken = tokens[blockIndex - 1];
        if (!prevToken) return;
        res = await apiClient.get('/tenants', { params: { nextToken: prevToken } });
      }
      const arr = Array.isArray(res.data) ? res.data : res.data.tenants;
      const nextToken = (res.data?.nextToken) ?? res.nextToken ?? null;
      const pagesCountInBlock = Math.ceil(arr.length / itemsPerPage);
      const newPagesData = { ...pagesData };
      for (let i = 0; i < pagesCountInBlock; i++) {
        const pageNumber = blockIndex * pagesPerFetch + i + 1;
        const startIdx = i * itemsPerPage;
        newPagesData[pageNumber] = arr.slice(startIdx, startIdx + itemsPerPage);
      }
      const newPageCounts = [...pageCounts];
      newPageCounts[blockIndex] = pagesCountInBlock;
      const newTokens = [...tokens];
      newTokens[blockIndex] = nextToken;
      setPagesData(newPagesData);
      setPageCounts(newPageCounts);
      setTokens(newTokens);
      setCurrentPage(desiredPage);
    } catch (err) {
      console.error(err);
      setErrorMessage('Failed to load tenants.');
      setShowErrorModal(true);
    }
  };

  const handlePageChange = (page) => {
    const blockIndex = Math.floor((page - 1) / pagesPerFetch);
    if (pageCounts[blockIndex]) {
      setCurrentPage(page);
    } else {
      loadBlock(blockIndex, page);
    }
    setOpenDropdownIndex(null);
  };

  const handleNextBlock = () => {
    const currentBlock = Math.floor((currentPage - 1) / pagesPerFetch);
    const nextBlock = currentBlock + 1;
    if (pageCounts[nextBlock]) {
      setCurrentPage(nextBlock * pagesPerFetch + 1);
    } else {
      loadBlock(nextBlock, nextBlock * pagesPerFetch + 1);
    }
  };

  const handlePrevBlock = () => {
    const currentBlock = Math.floor((currentPage - 1) / pagesPerFetch);
    const prevBlock = currentBlock - 1;
    setCurrentPage(prevBlock * pagesPerFetch + 1);
  };

  const handleAddTenantClick = () => setShowAddModal(true);
  const handleCloseAddTenant = () => setShowAddModal(false);

  const handleTenantNameClick = (tenant) => {
    setTenantInfo(tenant);
    setShowTenantInfoModal(true);
  };

  const handleAddTenant = async (tenantName, brandFile) => {
    try {
      let imageBase64 = '';
      let themeObject = null;

      if (brandFile) {
        const result = await validateZipContainsAllowedFiles(brandFile);
        imageBase64 = result.imageBase64 || '';
        themeObject = result.themeObject || null;
      }

      const newTenantObj = {
        TenantName: tenantName,
        BrandingLogo: imageBase64 || '',
      };

      const res = await apiClient.post('/tenants', newTenantObj);

      const tenantId = res.data?.TenantId;
      if (!tenantId) {
        throw new Error('Tenant creation failed. Missing TenantId.');
      }

      if (themeObject) {
        const brandingPayload = {
          ColorPrimary: themeObject['color-primary'],
          ColorSecondary: themeObject['color-secondary'],
          ColorBg: themeObject['color-bg'],
          ColorSidebarBg: themeObject['color-sidebar-bg'],
          ColorText: themeObject['color-text'],
          ColorLandingLeft: themeObject['color-landing-left'],
          ColorLandingRight: themeObject['color-landing-right'],
        };

        await apiClient.post('/branding', brandingPayload, {
          params: { tenantId },
        });
      }
      setPagesData({});
      setPageCounts([]);
      setTokens([]);
      setIsReloading(true);
      setShowAddModal(false);
    } catch (error) {
      setErrorMessage('Error adding tenant.');
      setShowErrorModal(true);
    }
  };

  const handleRemoveTenantClick = (tenant) => {
    setTenantToRemove(tenant);
    setShowRemoveConfirm(true);
  };

  const handleConfirmRemoveTenant = async () => {
    if (!tenantToRemove) return;
    try {
      await apiClient.delete(`/tenants/${tenantToRemove.TenantId}`);
      setPagesData({});
      setPageCounts([]);
      setTokens([]);
      setIsReloading(true);
    } catch (error) {
      setErrorMessage('Error removing tenant.');
      setShowErrorModal(true);
    } finally {
      setShowRemoveConfirm(false);
      setTenantToRemove(null);
    }
  };

  const handleEditTenant = (tenant) => {
    setEditTenant(tenant);
    setShowEditTenantModal(true);
  };

  const handleCloseEditTenant = () => {
    setShowEditTenantModal(false);
    setEditTenant(null);
  };

  const handleSaveTenant = async (originalTenant, newName, brandingFile = null) => {
    try {
      let imageBase64 = '';
      let themeObject = null;

      if (brandingFile) {
        const result = await validateZipContainsAllowedFiles(brandingFile);
        imageBase64 = result.imageBase64 || '';
        themeObject = result.themeObject || null;
      }

      const updateObj = { TenantName: newName };
      if (imageBase64) updateObj.BrandingLogo = imageBase64;

      await apiClient.put(`/tenants/${originalTenant.TenantId}`, updateObj);

      if (themeObject) {
        const brandingPayload = {
          ColorPrimary: themeObject['color-primary'],
          ColorSecondary: themeObject['color-secondary'],
          ColorBg: themeObject['color-bg'],
          ColorSidebarBg: themeObject['color-sidebar-bg'],
          ColorText: themeObject['color-text'],
          ColorLandingLeft: themeObject['color-landing-left'],
          ColorLandingRight: themeObject['color-landing-right'],
        };

        await apiClient.post('/branding', brandingPayload, {
          params: { tenantId: originalTenant.TenantId },
        });
      }

      const pageTenants = pagesData[currentPage] || [];
      const newPage = pageTenants.map((t) =>
        t.TenantId === originalTenant.TenantId ? { ...t, TenantName: newName } : t
      );
      setPagesData({ ...pagesData, [currentPage]: newPage });
      setShowEditTenantModal(false);
      setEditTenant(null);
    } catch (error) {
      setErrorMessage(error.message || 'Error saving tenant.');
      setShowErrorModal(true);
    }
  };

  const handleDropdownToggle = (index) => {
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  const handleExportTenants = async () => {
    try {
      const response = await apiClient.get('/export_tenants', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tenants.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setErrorMessage('Failed to export tenants.');
      setShowErrorModal(true);
    }
  };

  const validateZipContainsAllowedFiles = async (file) => {
    if (!file.name.endsWith('.zip')) {
      throw new Error('Uploaded file is not a ZIP file.');
    }

    const requiredKeys = [
      'color-primary',
      'color-secondary',
      'color-bg',
      'color-sidebar-bg',
      'color-text',
      'color-landing-left',
      'color-landing-right',
    ];

    const zip = await JSZip.loadAsync(file);
    const files = Object.keys(zip.files);

    let imageBase64 = '';
    let themeObject = null;

    for (const filename of files) {
      const ext = filename.split('.').pop().toLowerCase();

      if (['jpg', 'jpeg', 'png'].includes(ext) && !imageBase64) {
        const fileData = await zip.files[filename].async('base64');
        imageBase64 = fileData;
      }

      if (['xlsx', 'csv'].includes(ext) && !themeObject) {
        const binary = await zip.files[filename].async('arraybuffer');
        const workbook = XLSX.read(binary, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheetName];
        const parsed = XLSX.utils.sheet_to_json(sheet);

        if (!Array.isArray(parsed) || parsed.length === 0) {
          throw new Error('Excel/CSV file is empty.');
        }

        const theme = {};
        for (const row of parsed) {
          const key = row['Key'];
          const value = row['Value'];
          if (!key || !value) {
            throw new Error('Each row in the Excel/CSV file must have non-empty "Key" and "Value".');
          }
          theme[key] = value;
        }

        const missingKeys = requiredKeys.filter((key) => !(key in theme));
        if (missingKeys.length > 0) {
          throw new Error(`Missing keys in theme file: ${missingKeys.join(', ')}`);
        }

        themeObject = theme;
      }
    }

    if (!imageBase64 && !themeObject) {
      throw new Error('ZIP must contain at least an image or a valid Excel/CSV theme file.');
    }

    return { imageBase64, themeObject };
  };

  const handleSearchSubmit = async () => {
    if (!searchTerm.trim()) return;

    try {
      const response = await apiClient.get('/tenants', {
        params: { name: searchTerm.trim() }
      });

      const tenants = Array.isArray(response.data) ? response.data : response.data.tenants || [];
      setSearchResults(tenants);
      setCurrentPage(1);
    } catch (error) {
      console.error('Tenant search error:', error);
      setErrorMessage('Failed to fetch search results.');
      setShowErrorModal(true);
    }
  };


  const currentTenants = searchResults || pagesData[currentPage] || [];
  const filteredTenants = currentTenants.filter((tenant) => {
    if (!searchTerm.trim()) return true;
    const lower = searchTerm.toLowerCase();
    return (tenant.TenantName || '').toLowerCase().includes(lower);
  });

  const columns = ['TENANT', 'DEVICES', 'TENANT LOGO', ''];

  const tableRows = filteredTenants.map((t, index) => {
    const isLastRow = index === filteredTenants.length - 1;
    return {
      TENANT: (
        <div>
          <span
            data-testid="tenant-name"
            style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
            onClick={() => handleTenantNameClick(t)}
          >
            {t.TenantName}
          </span>
        </div>
      ),
      DEVICES: t.DeviceCount || 0,
      'TENANT LOGO': t.BrandingLogo ? (
        <a href={t.BrandingLogo} target="_blank" rel="noopener noreferrer">
          Logo
        </a>
      ) : (
        'N/A'
      ),
      '': (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div
            className="more-button"
            onClick={(e) => {
              e.stopPropagation();
              handleDropdownToggle(index);
            }}
            style={{ cursor: 'pointer' }}
          >
            ⋮
            {openDropdownIndex === index && (
              <Dropdown
                items={[
                  { label: 'Edit', onClick: () => handleEditTenant(t) },
                  { label: 'Remove', onClick: () => handleRemoveTenantClick(t), style: { color: 'red' } },
                ]}
                onClose={() => setOpenDropdownIndex(null)}
                placement={isLastRow ? 'top-left' : 'bottom-right'}
              />
            )}
          </div>
        </div>
      ),
    };
  });

  const currentBlockIndex = Math.floor((currentPage - 1) / pagesPerFetch);
  const startPage = currentBlockIndex * pagesPerFetch + 1;
  const pagesInBlock = pageCounts[currentBlockIndex] || 0;

  return (
    <div className="tenants-page">
      <h1>Tenants</h1>
      <div className="tenants-action-bar">
        <div className="tenants-left">
          <div className="tenants-search">
            <img src={tenantsSearch} className="users-search-icon" alt="search" />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            <button className="search-button" onClick={handleSearchSubmit}>Search</button>
            {searchResults && <button className="clear-button" onClick={() => setSearchResults(null)}>Clear</button>}
          </div>
        </div>
        <div className="tenants-right">
          <button className="add-button" onClick={handleAddTenantClick}>
            + Add Tenant
          </button>
          <button className="export-button" onClick={handleExportTenants}>
            Export
          </button>
        </div>
      </div>

      <Table
        columns={columns}
        data={tableRows}
        isLoading={isLoading || isReloading}
        placeholder="No tenants yet"
      />

      {!searchResults && (
        <div className="pagination">
          {currentBlockIndex > 0 && <button onClick={handlePrevBlock}>Prev</button>}
          {Array.from({ length: pagesInBlock }, (_, i) => startPage + i).map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={page === currentPage ? 'active' : ''}
            >
              {page}
            </button>
          ))}
          {tokens[currentBlockIndex] && <button onClick={handleNextBlock}>Next</button>}
        </div>
      )}

      {showAddModal && <AddTenantModal onClose={handleCloseAddTenant} onAdd={handleAddTenant} />}

      {showEditTenantModal && editTenant && (
        <EditTenantModal tenant={editTenant} onClose={handleCloseEditTenant} onSave={handleSaveTenant} />
      )}

      {showRemoveConfirm && tenantToRemove && (
        <ConfirmationModal
          isOpen={showRemoveConfirm}
          onCancel={() => setShowRemoveConfirm(false)}
          onConfirm={handleConfirmRemoveTenant}
          message={`Are you sure you want to remove tenant "${tenantToRemove?.TenantName}"?`}
          confirmLabel="Remove"
          isDanger={true}
        />
      )}

      {showTenantInfoModal && tenantInfo && (
        <ConfirmationModal
          isOpen={showTenantInfoModal}
          onCancel={() => setShowTenantInfoModal(false)}
          onConfirm={() => setShowTenantInfoModal(false)}
          message={
            <div style={{ textAlign: 'left' }}>
              <div>
                <strong>Name:</strong> {tenantInfo.TenantName}
              </div>
              <div>
                <strong>Branding:</strong> {tenantInfo.BrandingConfig}
              </div>
              <div>
                <strong>Devices:</strong> {tenantInfo.Devices}
              </div>
            </div>
          }
          confirmLabel="Close"
          showCancel={false}
        />
      )}

      {showErrorModal && (
        <ConfirmationModal
          isOpen={showErrorModal}
          onCancel={() => setShowErrorModal(false)}
          onConfirm={() => setShowErrorModal(false)}
          message={errorMessage}
          confirmLabel="OK"
          showCancel={false}
          isDanger={false}
        />
      )}
    </div>
  );
};

export default Tenants;